import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { QuoteRow } from "@/hooks/useQuotes";
import { cn } from "@/lib/utils";
import { formatMoney, overdueDays, type PaymentSummary } from "./drawerLogic";
import { CollectPaymentPanel } from "./CollectPaymentPanel";
import { PaidPaymentRow, PendingPaymentRow } from "./PaymentRow";
import { RecordPaymentPanel } from "./RecordPaymentPanel";
import {
  useDeleteQuotePayment,
  useMarkEtransferReceived,
  useSetPaymentDueDate,
  useStopWaitingEtransfer,
} from "./usePaymentActions";
import type { QuotePaymentRow } from "./useQuoteDrawerData";

export type PayMode = "collect" | "record" | null;

interface PaymentsSectionProps {
  quote: QuoteRow;
  /** summarizePayments(): paid and balance read quotes.amount_paid, never the rows. */
  summary: PaymentSummary;
  payments: QuotePaymentRow[];
  paymentsError: boolean;
  depositPct: number;
  etransferEmail: string | null;
  /** Lifted, so Next step's Collect / Record buttons open the panels here. */
  mode: PayMode;
  onModeChange: (mode: PayMode) => void;
}

/**
 * Balance due: a local value so a typed date shows at once; only a full date
 * writes. Keyed by the saved date, so a refetch starts it over.
 */
function DueDate({ quote, overdue }: { quote: QuoteRow; overdue: number | null }) {
  const id = useId();
  const [value, setValue] = useState(quote.payment_due_date ?? "");
  const { setDueDate } = useSetPaymentDueDate(quote.id);
  return (
    <>
      <label htmlFor={id} className={overdue ? "font-bold text-status-hot" : "text-muted-foreground"}>Balance due</label>
      <Input
        id={id}
        type="date"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          void setDueDate(e.target.value);
        }}
        className="h-7 w-auto px-2 text-[13px]"
      />
      {overdue && <span className="font-bold text-status-hot">{overdue} days overdue</span>}
    </>
  );
}

/** Payments on an accepted quote: bar, due date, rows, Collect and Record (#1559). */
export function PaymentsSection({ quote, summary, payments, paymentsError, depositPct, etransferEmail, mode, onModeChange }: PaymentsSectionProps) {
  const del = useDeleteQuotePayment(quote.id);
  const receive = useMarkEtransferReceived(quote);
  const stop = useStopWaitingEtransfer(quote);
  if (quote.status !== "accepted") return null;

  const total = Number(quote.total) || 0;
  const owing = summary.balance > 0.005;
  const overdue = overdueDays(quote.payment_due_date, summary.balance);
  const pending = summary.pendingAmount > 0 && quote.pending_etransfer_requested_at ? quote.pending_etransfer_requested_at : null;
  const close = () => onModeChange(null);

  return (
    <section data-section="payments" className="rounded-[10px] border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-3.5 py-2.5">
        <h3 className="font-display text-[15px] font-bold">Payments</h3>
        <span className={cn("font-bold tabular-nums", owing ? "text-status-warn" : "text-status-ok")}>
          {owing ? `${formatMoney(summary.balance)} left` : "Paid in full"}
        </span>
      </div>
      <div className="grid gap-2.5 px-3.5 py-3">
        <div role="img" aria-label={`${Math.round(summary.pct)}% paid`} className="h-2 overflow-hidden rounded-full bg-muted">
          <span className="block h-full bg-status-ok" style={{ width: `${summary.pct}%` }} />
        </div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-[13px] tabular-nums">
          <span className="text-muted-foreground">{formatMoney(summary.paid)} of {formatMoney(total)} paid</span>
          <span className="flex-1" />
          <DueDate key={quote.payment_due_date ?? ""} quote={quote} overdue={overdue} />
        </div>
        {paymentsError && <p className="text-[12.5px] text-muted-foreground">Couldn't load payments.</p>}
        {(pending || payments.length > 0) && (
          <div className="grid gap-1.5 text-[13.5px] tabular-nums">
            {pending && (
              <PendingPaymentRow
                amount={summary.pendingAmount}
                requestedAt={pending}
                balance={summary.balance}
                onReceive={receive.receive}
                onStop={stop.stop}
                receiving={receive.receiving}
                stopping={stop.stopping}
              />
            )}
            {payments.map((p) => (
              <PaidPaymentRow key={p.id} payment={p} onDelete={del.remove} deleting={del.removing} />
            ))}
          </div>
        )}
        {mode === "collect" ? (
          <CollectPaymentPanel quote={quote} summary={summary} depositPct={depositPct} etransferEmail={etransferEmail} onClose={close} />
        ) : mode === "record" ? (
          <RecordPaymentPanel quote={quote} summary={summary} onClose={close} />
        ) : (
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onModeChange("collect")}>Collect payment</Button>
            <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => onModeChange("record")}>+ Record payment</Button>
          </div>
        )}
      </div>
    </section>
  );
}
